import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Typography, Box, CircularProgress, Alert, IconButton, Tooltip } from "@mui/material";
import FlexColumnBox from "../../components/common/FlexBox";
import DataComparisonGrid from "../../components/common/DataComparisonGrid";
import WeekDatePicker from "../../components/common/WeekDatePicker";
import HotelSelector from "../../components/common/HotelSelector";
import HotelDataStatusList from "../../components/common/HotelDataStatusList";
import { useHotels } from "../../hooks/useHotels";
import { useAuth } from "../../context/authContext";
import { useDebounce } from "../../utils/useDebounce";
import StatService from "../../api/statService";
import { addDaysToDate } from "../../utils/dateUtils";
import WeeklyOccupancyTable from "../../components/common/WeeklyOccupancyTable";
import WeeklyForecastTable from "../../components/common/WeeklyForecastTable";
import WeeklyComparisonTable from "../../components/common/WeeklyComparisonTable";
import { buildGrillesAvtCsv } from "../../utils/grillesCsv";
import { downloadCsv, formatCsvFilename } from "../../utils/csvDownload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { exportGrillesCompletXlsx } from "../../utils/grillesXlsx";

type NamedData = { name: string; data: string | number };
type ComparisonData = {
  forecastData: { current: NamedData[]; previous: NamedData[] };
  realData: { current: NamedData[]; previous: NamedData[] };
  visitorData: { current: NamedData[]; previous: NamedData[] };
};

const resetDateToFriday = (date: Date) => {
  const dayOfWeek = date.getDay();
  const offset = (5 - dayOfWeek + 7) % 7;
  const adjusted = new Date(date);
  adjusted.setDate(adjusted.getDate() + offset);
  adjusted.setHours(0, 0, 0, 0);
  return adjusted;
};

const formatWeeklyRows = (data: any[], start: Date) => {
  const rows = [];
  for (let i = 0; i < 7; i++) {
    const d = addDaysToDate(start, i);
    const dateStr = d.toISOString().split("T")[0];
    const dailyItems = data.filter((x) => x.date === dateStr);
    const sold = dailyItems.reduce(
      (sum, item) => sum + (item.sold_Units || 0),
      0
    );
    const available = dailyItems.reduce(
      (sum, item) => sum + (item.available_Units || 0),
      0
    );
    const visitors = dailyItems.reduce(
      (sum, item) => sum + (item.number_Of_Visitors || 0),
      0
    );
    rows.push({
      nights: sold,
      people: visitors,
      occupancyRate: available > 0 ? Number(((sold / available) * 100).toFixed(2)) : 0,
    });
  }
  return rows;
};

const buildVisitorData = (visitors: any[]): NamedData[] => {
  const map = new Map<string, number>();
  visitors.forEach((v) => {
    const name = v.visitorTypeName?.trim() || `Type ${v.id}`;
    map.set(name, (map.get(name) ?? 0) + v.total);
  });
  const list: NamedData[] = Array.from(map.entries()).map(([k, v]) => ({
    name: k,
    data: v,
  }));
  const total = visitors.reduce((sum, v) => sum + (v.total || 0), 0);
  list.push({ name: "Total visiteurs", data: total });
  return list;
};

const GrillesAVT = () => {
  const hotels = useHotels();
  const { user } = useAuth();
  const initialStart = resetDateToFriday(new Date());

  const [localStartDate, setLocalStartDate] = useState(initialStart);
  
  const [startDate, setStartDate] = useState(initialStart);
  const [selectedHotel, setSelectedHotel] = useState<any | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const applyDate = useCallback((date: Date) => {
    setStartDate(date);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const debouncedSetDate = useCallback((date: Date) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setStartDate(date);
    }, 3000);
  }, []);
  const [comparisonData, setComparisonData] = useState<ComparisonData>({
    forecastData: { current: [], previous: [] },
    realData: { current: [], previous: [] },
    visitorData: { current: [], previous: [] },
  });
  const [weeklyOccupancyData, setWeeklyOccupancyData] = useState<any[]>([]);
  const [weeklyForecastData, setWeeklyForecastData] = useState<any[]>([]);
  const [forecastComparisonWeek1, setForecastComparisonWeek1] = useState<any>(null);
  const [forecastComparisonWeek2, setForecastComparisonWeek2] = useState<any>(null);
  const [comparisonTableData, setComparisonTableData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestCounter = useRef(0);
  const currentYear = useMemo(() => startDate.getFullYear(), [startDate]);
  const [hotelDataStatus, setHotelDataStatus] = useState<
    Array<{ id: number; name: string; hasData: boolean }>
  >([]);

  useEffect(() => {
    // If user has an associated hotel (non-admin), lock to that
    if (user?.role !== "Admin" && user?.hotel) {
      setSelectedHotel(user.hotel);
      return;
    }
    // If admin or user without hotel, show all (null => all hotels)
    if (user && (!user.hotel || user.role === "Admin")) {
      setSelectedHotel(null);
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    const currentRequest = ++requestCounter.current;
    setLoading(true);
    setError(null);
    // Clear previous data immediately to avoid showing stale content when new query yields no rows
    setWeeklyOccupancyData([]);
    setWeeklyForecastData([]);
    setComparisonTableData(null);
    setHotelDataStatus([]);
    setComparisonData({
      forecastData: { current: [], previous: [] },
      realData: { current: [], previous: [] },
      visitorData: { current: [], previous: [] },
    });

    const endDate = addDaysToDate(startDate, 6);
    const prevStart = addDaysToDate(startDate, -365);
    const prevEnd = addDaysToDate(endDate, -365);
    // Pour les prévisions, on récupère 2 semaines (14 jours)
    const forecastEndDate = addDaysToDate(startDate, 13);
    // Pour la comparaison de la semaine 2, on a besoin de l'année précédente
    const prevForecastStart = addDaysToDate(startDate, -365);
    const prevForecastEnd = addDaysToDate(forecastEndDate, -365);

    try {
      const [forecast, forecastPrev, realNow, realPrev, visitorsNow, visitorsPrev] =
        await Promise.all([
          StatService.getForecast(
            selectedHotel?.id ?? null,
            startDate,
            forecastEndDate
          ),
          StatService.getForecast(
            selectedHotel?.id ?? null,
            prevForecastStart,
            prevForecastEnd
          ),
          StatService.getOccupation(
            selectedHotel?.id ?? null,
            startDate,
            endDate
          ),
          StatService.getOccupation(
            selectedHotel?.id ?? null,
            prevStart,
            prevEnd
          ),
          StatService.getVisitors(
            selectedHotel?.id ?? null,
            startDate,
            endDate
          ),
          StatService.getVisitors(
            selectedHotel?.id ?? null,
            prevStart,
            prevEnd
          ),
        ]);

      if (currentRequest !== requestCounter.current) return;

      const getTotals = (
        data: any[],
        referenceStartDate: Date
      ) => {
        let occupied = 0;
        let available = 0;
        let visitors = 0;

        for (let i = 0; i < 7; i++) {
          const d = addDaysToDate(referenceStartDate, i)
            .toISOString()
            .split("T")[0];
          const dayItems = data.filter((x) => x.date === d);
          if (dayItems.length > 0) {
            // eslint-disable-next-line no-loop-func
            dayItems.forEach((item) => {
              occupied += item.sold_Units ?? 0;
              available += item.available_Units ?? 0;
              visitors += item.number_Of_Visitors ?? 0;
            });
          }
        }

        return { occupied, available, visitors };
      };

      const currentTotals = getTotals(realNow, startDate);
      const previousTotals = getTotals(realPrev, prevStart);

      setComparisonTableData({
        availableNights: {
          current: currentTotals.available,
          previous: previousTotals.available,
        },
        occupiedNights: {
          current: currentTotals.occupied,
          previous: previousTotals.occupied,
        },
        persons: {
          current: currentTotals.visitors,
          previous: previousTotals.visitors,
        },
        averageOccupancy: {
          current: currentTotals.available > 0
            ? Number(((currentTotals.occupied / currentTotals.available) * 100).toFixed(2))
            : 0,
          previous: previousTotals.available > 0
            ? Number(((previousTotals.occupied / previousTotals.available) * 100).toFixed(2))
            : 0,
        },
      });

      setWeeklyOccupancyData(
        formatWeeklyRows(realNow, startDate)
      );

      // Formater les données de prévisions sur 2 semaines (14 jours)
      const forecastRows = [];
      for (let i = 0; i < 14; i++) {
        const d = addDaysToDate(startDate, i);
        const dateStr = d.toISOString().split("T")[0];
        const dailyItems = forecast.filter((x) => x.date === dateStr);
        const sold = dailyItems.reduce(
          (sum, item) => sum + (item.sold_Units || 0),
          0
        );
        const available = dailyItems.reduce(
          (sum, item) => sum + (item.available_Units || 0),
          0
        );
        const visitors = dailyItems.reduce(
          (sum, item) => sum + (item.number_Of_Visitors || 0),
          0
        );
        forecastRows.push({
          nights: sold,
          people: visitors,
          occupancyRate: available > 0 ? Number(((sold / available) * 100).toFixed(2)) : 0,
        });
      }
      setWeeklyForecastData(forecastRows);

      // Calculer les données de comparaison pour la semaine 1 (jours 0-6)
      const getForecastTotals = (
        data: any[],
        referenceStartDate: Date,
        dayCount: number
      ) => {
        let occupied = 0;
        let available = 0;
        let visitors = 0;

        for (let i = 0; i < dayCount; i++) {
          const d = addDaysToDate(referenceStartDate, i)
            .toISOString()
            .split("T")[0];
          const dayItems = data.filter((x) => x.date === d);
          if (dayItems.length > 0) {
            dayItems.forEach((item) => {
              occupied += item.sold_Units ?? 0;
              available += item.available_Units ?? 0;
              visitors += item.number_Of_Visitors ?? 0;
            });
          }
        }

        return { occupied, available, visitors };
      };

      // Semaine 1 : jours 0-6 depuis startDate
      const week1CurrentTotals = getForecastTotals(forecast, startDate, 7);
      const week1PreviousTotals = getForecastTotals(forecastPrev, prevForecastStart, 7);

      setForecastComparisonWeek1({
        availableNights: {
          current: week1CurrentTotals.available,
          previous: week1PreviousTotals.available,
        },
        occupiedNights: {
          current: week1CurrentTotals.occupied,
          previous: week1PreviousTotals.occupied,
        },
        persons: {
          current: week1CurrentTotals.visitors,
          previous: week1PreviousTotals.visitors,
        },
        averageOccupancy: {
          current: week1CurrentTotals.available > 0
            ? Number(((week1CurrentTotals.occupied / week1CurrentTotals.available) * 100).toFixed(2))
            : 0,
          previous: week1PreviousTotals.available > 0
            ? Number(((week1PreviousTotals.occupied / week1PreviousTotals.available) * 100).toFixed(2))
            : 0,
        },
      });

      // Semaine 2 : jours 7-13 depuis startDate
      const week2StartDate = addDaysToDate(startDate, 7);
      const week2PrevStartDate = addDaysToDate(prevForecastStart, 7);
      const week2CurrentTotals = getForecastTotals(forecast, week2StartDate, 7);
      const week2PreviousTotals = getForecastTotals(forecastPrev, week2PrevStartDate, 7);

      setForecastComparisonWeek2({
        availableNights: {
          current: week2CurrentTotals.available,
          previous: week2PreviousTotals.available,
        },
        occupiedNights: {
          current: week2CurrentTotals.occupied,
          previous: week2PreviousTotals.occupied,
        },
        persons: {
          current: week2CurrentTotals.visitors,
          previous: week2PreviousTotals.visitors,
        },
        averageOccupancy: {
          current: week2CurrentTotals.available > 0
            ? Number(((week2CurrentTotals.occupied / week2CurrentTotals.available) * 100).toFixed(2))
            : 0,
          previous: week2PreviousTotals.available > 0
            ? Number(((week2PreviousTotals.occupied / week2PreviousTotals.available) * 100).toFixed(2))
            : 0,
        },
      });

      setComparisonData({
        forecastData: {
          current: [
            {
              name: "Nuitées prévues",
              data: forecast.reduce((acc, f) => acc + (f.sold_Units || 0), 0),
            },
            {
              name: "Taux prévu",
              data:
                forecast.length > 0
                  ? (() => {
                      const totalSold = forecast.reduce((a, f) => a + (f.sold_Units || 0), 0);
                      const totalAvailable = forecast.reduce((a, f) => a + (f.available_Units || 0), 0);
                      return totalAvailable > 0 ? `${((totalSold / totalAvailable) * 100).toFixed(2)}%` : "N/A";
                    })()
                  : "N/A",
            },
          ],
          previous: [
            { name: "Nuitées réelles", data: currentTotals.occupied },
            {
              name: "Taux réel",
              data:
                currentTotals.available > 0
                  ? `${((currentTotals.occupied / currentTotals.available) * 100).toFixed(2)}%`
                  : "N/A",
            },
          ],
        },
        realData: {
          current: [
            { name: "Nuitées réelles", data: currentTotals.occupied },
            {
              name: "Taux réel",
              data:
                currentTotals.available > 0
                  ? `${((currentTotals.occupied / currentTotals.available) * 100).toFixed(2)}%`
                  : "N/A",
            },
          ],
          previous: [
            { name: "Nuitées N-1", data: previousTotals.occupied },
            {
              name: "Taux N-1",
              data:
                previousTotals.available > 0
                  ? `${((previousTotals.occupied / previousTotals.available) * 100).toFixed(2)}%`
                  : "N/A",
            },
          ],
        },
        visitorData: {
          current: buildVisitorData(visitorsNow),
          previous: buildVisitorData(visitorsPrev),
        },
      });

      if (!selectedHotel && hotels.length > 0) {
        const statusList = hotels.map((hotel) => {
          const hasForecastData = forecast.some((item: any) => item.fK_Hotel_ID === hotel.id && item.fK_Hotel_ID > 0);
          const hasRealData = realNow.some((item: any) => item.fK_Hotel_ID === hotel.id && item.fK_Hotel_ID > 0);
          const hasVisitorData = visitorsNow.some((item: any) => item.hotelId === hotel.id && item.hotelId > 0);
          return {
            id: hotel.id,
            name: hotel.name,
            hasData: hasForecastData || hasRealData || hasVisitorData,
          };
        });
        setHotelDataStatus(statusList);
      }
    } catch (error) {
      if (currentRequest === requestCounter.current) {
        console.error("Erreur lors de la récupération des données :", error);
        setError("Impossible de charger les données.");
      }
    } finally {
      if (currentRequest === requestCounter.current) {
        setLoading(false);
      }
    }
  }, [startDate, selectedHotel, hotels]);

  const debouncedFetch = useDebounce(fetchData, 200);

  useEffect(() => {
    debouncedFetch();
  }, [selectedHotel, startDate, debouncedFetch]);

  const handleExportCsv = () => {
    const includedHotelNames = selectedHotel
      ? [selectedHotel.name]
      : hotels.map((h) => h.name);

    const csv = buildGrillesAvtCsv({
      currentYear,
      startDate,
      comparisonData,
      weeklyOccupancyData,
      weeklyComparisonData: comparisonTableData,
      includedHotelNames,
    });

    const filename = formatCsvFilename("grilles_avt", startDate, addDaysToDate(startDate, 6));
    downloadCsv(filename, csv);
  };

  const handleExportXlsx = async () => {
    try {
      const includedHotelNames = selectedHotel
        ? [selectedHotel.name]
        : hotelDataStatus
            .filter(h => h.hasData)
            .map(h => h.name);
      
      await exportGrillesCompletXlsx({
        startDate,
        endDate: addDaysToDate(startDate, 6),
        weeklyForecastData,
        weeklyOccupancyData,
        comparisonWeek1: forecastComparisonWeek1,
        comparisonWeek2: forecastComparisonWeek2,
        comparisonTableData,
        comparisonData,
        hotelNames: includedHotelNames,
      });
    } catch (e) {
      setError("Impossible de générer le fichier XLSX. Assurez-vous que le template est présent dans /public/GRILLES_INFO_TEMPLATE.xlsx.");
    }
  };

  return (
    <FlexColumnBox margin="auto" mt={5} alignItems="center" width="100%">
      <Box
        width="95%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        mb={3}
      >
        <Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
          Grilles Info-Villégiature
        </Typography>
        <Box sx={{ position: "absolute", right: 0, display: "flex", gap: 1 }}>
          <Tooltip title="Exporter les données au format XLSX">
            <span>
              <IconButton 
                color="primary" 
                onClick={handleExportXlsx} 
                size="large"
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        display="flex"
        gap={2}
        mb={4}
        flexWrap="wrap"
        justifyContent="center"
      >
        <HotelSelector
          hotels={hotels}
          selectedHotel={selectedHotel}
          onChange={(h) => setSelectedHotel(h)}
          disabled={user?.role !== "Admin" && !!user?.hotel}
          includeAllOption
        />
        <WeekDatePicker
          startDate={localStartDate}
          setStartDate={(date) => {
            const aligned = resetDateToFriday(date);
            setLocalStartDate(aligned);
            debouncedSetDate(aligned);
          }}
          onApply={(date) => {
            const aligned = resetDateToFriday(date);
            setLocalStartDate(aligned);
            applyDate(aligned);
          }}
        />
      </Box>

      {loading ? (
        <Box mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!error && weeklyOccupancyData.length === 0 && !comparisonTableData && (
            <Box mt={4}>
              <Alert severity="info">Aucune donnée pour la période sélectionnée.</Alert>
            </Box>
          )}
          {error && (
            <Box mt={4}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {!selectedHotel && hotelDataStatus.length > 0 && (
            <HotelDataStatusList hotels={hotelDataStatus} />
          )}

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            flexWrap="wrap"
            gap={3}
            mt={5}
            mb={5}
          >
            <DataComparisonGrid
              title={`Occupation prévue vs réelle - ${currentYear}`}
              leftSubtitle={`Prévision ${currentYear}`}
              rightSubtitle={`Réelle ${currentYear}`}
              leftData={comparisonData.forecastData.current}
              rightData={comparisonData.forecastData.previous}
            />
            <DataComparisonGrid
              title={`Occupation réelle ${currentYear} vs ${currentYear - 1}`}
              leftSubtitle={`Réelle ${currentYear}`}
              rightSubtitle={`Réelle ${currentYear - 1}`}
              leftData={comparisonData.realData.current}
              rightData={comparisonData.realData.previous}
            />
            <DataComparisonGrid
              title="Achalandage destination"
              leftSubtitle={`Visiteurs ${currentYear}`}
              rightSubtitle={`Visiteurs ${currentYear - 1}`}
              leftData={comparisonData.visitorData.current}
              rightData={comparisonData.visitorData.previous}
            />
          </Box>

          {weeklyForecastData.length > 0 && (
            <WeeklyForecastTable
                startDate={startDate}
                data={weeklyForecastData}
                currentYear={currentYear}
                comparisonWeek1={forecastComparisonWeek1}
                comparisonWeek2={forecastComparisonWeek2}
              />
          )}
        </>
      )}
    </FlexColumnBox>
  );
};

export default GrillesAVT;
